/**
 * JurisdictionsAdmin — Settings section for the permit "Jurisdiction" picklist
 * (the metro cities that issue install permits). Add / rename / archive, self-
 * serve so the office runs it without code edits. A search box keeps the 120+
 * seeded cities manageable. This list also becomes the per-city home for the
 * CX-01 after-install inspection instructions.
 */
import { useMemo, useState } from "react";
import { Check, Landmark, Pencil, Plus, RotateCcw, Search, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import {
  useAllJurisdictions,
  useAddJurisdiction,
  useUpdateJurisdiction,
} from "./jurisdictions";

export function JurisdictionsAdmin() {
  const { data, isLoading } = useAllJurisdictions();
  const add = useAddJurisdiction();
  const update = useUpdateJurisdiction();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const rows = data ?? [];
  const needle = q.trim().toLowerCase();
  const active = useMemo(
    () => rows.filter((r) => r.active && (!needle || r.name.toLowerCase().includes(needle))),
    [rows, needle],
  );
  const archived = rows.filter((r) => !r.active);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-sky-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <Landmark className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] font-bold text-slate-800">
            Permit Cities
          </span>
          <InfoTip label="About permit cities">
            The Jurisdiction list on a job&apos;s permit. Archiving a city hides it from the picker; permits already tagged with
            it keep the name.
          </InfoTip>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900"
        >
          <Plus className="h-3 w-3" />
          Add city
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(name, { onSuccess: () => { setName(""); setAdding(false); } });
            }}
            className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2"
          >
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maple Grove"
              className="min-w-[160px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <button
              type="submit"
              disabled={!name.trim() || add.isPending}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {add.isPending ? "Saving…" : "Add"}
            </button>
            {add.isError && (
              <span className="text-xs text-red-600">{(add.error as Error).message}</span>
            )}
          </form>
        )}

        {/* Search — 120+ seeded cities */}
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cities…"
            className="w-full rounded-md border border-hairline bg-white py-1.5 pl-8 pr-3 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
        </div>
        <p className="mb-2 text-[11px] text-slate-400">
          {active.length} {needle ? (active.length === 1 ? "match" : "matches") : "cities"}
          {archived.length > 0 && ` · ${archived.length} archived`}
        </p>

        {isLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : active.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            {needle ? `No cities match "${q.trim()}".` : "No cities yet — add one above."}
          </p>
        ) : (
          <ul className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
            {active.map((c) => (
              <li key={c.id} className="group flex items-center gap-2 py-1.5">
                {editId === c.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      update.mutate(
                        { id: c.id, name: editName },
                        { onSuccess: () => setEditId(null) },
                      );
                    }}
                    className="flex flex-1 items-center gap-2"
                  >
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 rounded-md border border-hairline px-2 py-1 text-sm"
                    />
                    <button type="submit" className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white">
                      <Check className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:text-slate-600">
                      <X className="h-3 w-3" />
                    </button>
                  </form>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                      {c.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setEditId(c.id); setEditName(c.name); }}
                      title="Rename"
                      className="rounded p-1 text-slate-300 opacity-40 transition-all hover:text-slate-600 group-hover:opacity-100"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => update.mutate({ id: c.id, active: false })}
                      title="Archive — hides it from the picker; existing permits keep the name"
                      className="rounded p-1 text-slate-300 opacity-40 transition-all hover:text-red-500 group-hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400 hover:text-slate-600"
            >
              {showArchived ? "Hide" : "Show"} archived ({archived.length})
            </button>
            {showArchived && (
              <ul className="space-y-1">
                {archived.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="min-w-0 flex-1 truncate line-through">{c.name}</span>
                    <button
                      type="button"
                      onClick={() => update.mutate({ id: c.id, active: true })}
                      title="Restore"
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50",
                      )}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
